import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import NotificationBadge from '../../../components/ui/NotificationBadge';
import Button from '../../../components/ui/Button';

const NotificationCenter = ({
  notifications = [],
  unreadCount = 0,
  onMarkAsRead = () => {},
  onArchive = () => {}
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const getNotificationConfig = (type) => {
    const configs = {
      workshop: { icon: 'BookOpen', color: 'text-accent', bgColor: 'bg-accent/10' },
      announcement: { icon: 'Megaphone', color: 'text-primary', bgColor: 'bg-primary/10' },
      university: { icon: 'School', color: 'text-primary', bgColor: 'bg-primary/10' },
      tutoring: { icon: 'Users', color: 'text-secondary', bgColor: 'bg-secondary/10' },
      system: { icon: 'Bell', color: 'text-warning', bgColor: 'bg-warning/10' }
    };
    return configs?.[type] || configs?.system;
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notifDate = new Date(timestamp);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleMarkAsRead = (id, isRead) => {
    if (!isRead) {
      onMarkAsRead(id);
    }
  };

  const handleArchive = (id) => {
    onArchive(id);
  };

  const handleNotificationClick = (notification) => {
    const { type, metadata } = notification;
    
    // Mark as read
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (type === 'announcement' && metadata?.workshop_id) {
      navigate(`/workshop-announcements/${metadata.workshop_id}`);
    } else if (type === 'workshop' && metadata?.workshop_id) {
      navigate('/workshop-catalog');
    }
  };

  const displayedNotifications = isExpanded ? notifications : notifications?.slice(0, 3);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
          {unreadCount > 0 && (
            <NotificationBadge count={unreadCount} variant="error" size="default" />
          )}
        </div>
        {notifications?.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary hover:text-primary/80 font-medium transition-hover"
          >
            {isExpanded ? 'Show Less' : `View All (${notifications.length})`}
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {notifications?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="BellOff" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
            <p className="text-xs text-muted-foreground mt-1">We'll notify you about important updates</p>
          </div>
        ) : (
          displayedNotifications?.map((notification) => {
            const config = getNotificationConfig(notification?.type);
            const isRead = notification?.is_read || false;

            return (
              <div
                key={notification?.id}
                className={`flex items-start gap-3 p-3 rounded-lg transition-smooth group cursor-pointer ${
                  isRead ? 'bg-muted/20' : 'bg-muted/50 border border-border'
                } hover:bg-muted/60`}
                onClick={() => handleNotificationClick(notification)}
              >
                {/* Icon */}
                <div className={`${config?.bgColor} ${config?.color} p-2 rounded-full flex-shrink-0 mt-0.5`}>
                  <Icon name={config?.icon} size={16} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p
                      className={`text-sm ${
                        isRead
                          ? 'text-muted-foreground'
                          : 'text-foreground font-medium'
                      } line-clamp-2`}
                    >
                      {notification?.message}
                    </p>
                    {!isRead && (
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatNotificationTime(notification?.created_at)}
                  </span>
                </div>

                {/* Action Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchive(notification.id);
                  }}
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                  title="Archive notification"
                >
                  <Icon name="X" size={16} className="text-muted-foreground" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Footer - View All Button */}
      {notifications?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All Notifications
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;